import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { apiAdminGetAgentMemberBets, apiAdminGetPartner, ApiRequestError } from '@/lib/api'
import type { AgentBetItem, PaginatedResult, RoundResult } from '@nexbit/types'
import { formatCurrency } from '@/lib/utils'
import { format } from 'date-fns'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, ArrowLeft, ChevronLeft, ChevronRight, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'

function resultLabel(betType: string, result: RoundResult | null): string | null {
  if (!result) return null
  const isHighLow = betType === 'high' || betType === 'low'
  return isHighLow ? result.highLow : result.oddEven
}

function betStatusVariant(status: string): 'success' | 'destructive' | 'warning' | 'outline' {
  if (status === 'won') return 'success'
  if (status === 'lost') return 'destructive'
  if (status === 'pending') return 'warning'
  return 'outline'
}

function RoundResultDialog({ bet, open, onClose }: { bet: AgentBetItem | null; open: boolean; onClose: () => void }) {
  if (!bet) return null
  const result = bet.result
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm uppercase tracking-wider">
            ผลรอบ — {bet.gameCode}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 mt-1">
          <div className="flex flex-wrap gap-2">
            <div className="flex flex-col gap-0.5 flex-1 min-w-[100px] border border-border rounded-[var(--radius-sm)] px-3 py-2 bg-secondary/30">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">บิลนี้</span>
              <span className="font-mono text-sm font-semibold uppercase">
                {bet.betType}{bet.number ? ` ${bet.number}` : ''}
              </span>
              <span className="font-mono text-xs text-muted-foreground">{formatCurrency(bet.amount)}</span>
            </div>
            {result ? (
              <>
                <div className="flex flex-col gap-0.5 flex-1 min-w-[100px] border border-border rounded-[var(--radius-sm)] px-3 py-2 bg-secondary/30">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">สูง/ต่ำ</span>
                  <span className={cn('font-mono text-sm font-semibold uppercase', result.highLow === 'high' ? 'text-success' : 'text-destructive')}>
                    {result.highLow}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5 flex-1 min-w-[100px] border border-border rounded-[var(--radius-sm)] px-3 py-2 bg-secondary/30">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">คู่/คี่</span>
                  <span className="font-mono text-sm font-semibold uppercase">{result.oddEven}</span>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground font-mono text-xs">ยังไม่ตัดสิน</div>
            )}
          </div>

          {result?.digits && Object.keys(result.digits).length > 0 && (
            <div className="border border-border rounded-[var(--radius-sm)] overflow-hidden">
              <div className="px-3 py-1.5 bg-secondary">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">เลขท้ายแต่ละเหรียญ</span>
              </div>
              <div className="divide-y divide-border">
                {Object.entries(result.digits).map(([coin, digit]) => (
                  <div key={coin} className="flex items-center justify-between px-3 py-1.5">
                    <span className="font-mono text-xs text-muted-foreground">{coin}</span>
                    <div className="flex items-center gap-3">
                      {result.prices?.[coin] && (
                        <span className="font-mono text-xs text-muted-foreground">{parseFloat(result.prices![coin]).toLocaleString()}</span>
                      )}
                      <span className="font-mono text-sm font-bold">{digit}</span>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between px-3 py-1.5 bg-secondary/30">
                  <span className="font-mono text-xs font-semibold">รวม</span>
                  <span className="font-mono text-sm font-bold">
                    {Object.values(result.digits).reduce((s, d) => s + d, 0)}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="font-mono text-xs text-muted-foreground">สถานะ</span>
            <Badge variant={betStatusVariant(bet.status)} className="font-mono text-[10px]">{bet.status}</Badge>
          </div>
          {bet.winAmount && (
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs text-muted-foreground">ยอดจ่าย</span>
              <span className="font-mono text-sm font-semibold text-success">{formatCurrency(bet.winAmount)}</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default function AdminReportsAgentMemberPage() {
  const t = useT()
  const { partnerId, memberId } = useParams<{ partnerId: string; memberId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const from = searchParams.get('from') ?? ''
  const to = searchParams.get('to') ?? ''
  const username = searchParams.get('username') ?? memberId ?? ''
  const agentShareRateParam = parseFloat(searchParams.get('agentShareRate') ?? '0')

  const [data, setData] = useState<PaginatedResult<AgentBetItem> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [agentShareRate, setAgentShareRate] = useState(agentShareRateParam)
  const [detailBet, setDetailBet] = useState<AgentBetItem | null>(null)

  useEffect(() => {
    if ((agentShareRateParam > 0 && !isNaN(agentShareRateParam)) || !partnerId) return
    void apiAdminGetPartner(partnerId).then((p) => {
      setAgentShareRate(parseFloat(p.commissionRate))
    }).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const load = useCallback(async (p: number) => {
    if (!partnerId || !memberId || !from || !to) { setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      setData(await apiAdminGetAgentMemberBets(partnerId, memberId, { from, to, page: p, limit: 50 }))
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [partnerId, memberId, from, to, t])

  useEffect(() => { void load(page) }, [load, page])

  const totalPages = data ? Math.ceil(data.total / data.limit) : 0

  function handleBack() {
    const q = new URLSearchParams({ from, to })
    navigate(`/admin/reports/agent/${partnerId ?? ''}?${q.toString()}`)
  }

  const showShares = agentShareRate > 0

  const totals = data?.items.reduce(
    (acc, b) => {
      const net = parseFloat(b.amount) - parseFloat(b.winAmount ?? '0')
      return {
        bet: acc.bet + parseFloat(b.amount),
        win: acc.win + parseFloat(b.winAmount ?? '0'),
        net: acc.net + net,
        aNet: acc.aNet + net * agentShareRate,
        dNet: acc.dNet + net * (1 - agentShareRate),
      }
    },
    { bet: 0, win: 0, net: 0, aNet: 0, dNet: 0 },
  ) ?? null

  return (
    <div className="p-4 md:p-6 max-w-6xl">
      <div className="flex items-center gap-3 mb-5">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 font-mono text-xs text-muted-foreground"
          onClick={handleBack}
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t.betReport.backToReport}
        </Button>
        <span className="text-muted-foreground/40">·</span>
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
          {username} — {t.betReport.memberBets}
        </span>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
        <div className="px-4 py-2.5 bg-secondary flex items-center justify-between">
          <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
            {t.betReport.memberBets}
          </span>
          {data && !loading && (
            <span className="font-mono text-xs text-secondary-foreground/60">{data.total} {t.betReport.betCount}</span>
          )}
        </div>

        {loading ? (
          <div className="divide-y divide-border">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-4 px-4 py-3">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="py-12 text-center">
            <p className="font-mono text-sm text-muted-foreground">{t.betReport.noActivity}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.betReport.date}</TableHead>
                <TableHead>{t.betReport.game}</TableHead>
                <TableHead>{t.betReport.betType}</TableHead>
                <TableHead className="text-right">{t.betReport.betVolume}</TableHead>
                <TableHead className="text-right">{t.betReport.payout}</TableHead>
                <TableHead>{t.betReport.result}</TableHead>
                <TableHead>{t.common.status}</TableHead>
                <TableHead className="text-right">{t.betReport.net}</TableHead>
                {showShares && (
                  <>
                    <TableHead className="text-right">{t.reports.agentNet}</TableHead>
                    <TableHead className="text-right">{t.reports.adminNet}</TableHead>
                  </>
                )}
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((b) => {
                const won = b.winAmount !== null && parseFloat(b.winAmount) > 0
                const houseNet = parseFloat(b.amount) - parseFloat(b.winAmount ?? '0')
                return (
                  <TableRow key={b.id}>
                    <TableCell>
                      <time dateTime={b.createdAt} className="font-mono text-xs text-muted-foreground">
                        {format(new Date(b.createdAt), 'dd/MM HH:mm')}
                      </time>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{b.gameCode}</TableCell>
                    <TableCell className="font-mono text-xs uppercase">
                      {b.betType}
                      {b.number && <span className="ml-1.5 font-semibold text-foreground">{b.number}</span>}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatCurrency(b.amount)}</TableCell>
                    <TableCell className={cn('text-right font-mono text-sm', won ? 'text-success font-semibold' : 'text-muted-foreground')}>
                      {b.winAmount ? formatCurrency(b.winAmount) : '—'}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const label = resultLabel(b.betType, b.result)
                        return label ? (
                          <Badge variant="outline" className="font-mono text-[10px] uppercase">{label}</Badge>
                        ) : '—'
                      })()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={betStatusVariant(b.status)} className="font-mono text-[10px]">
                        {b.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      <span className={houseNet >= 0 ? 'text-success font-semibold' : 'text-destructive font-semibold'}>
                        {formatCurrency(houseNet.toFixed(2))}
                      </span>
                    </TableCell>
                    {showShares && (() => {
                      const aNet = houseNet * agentShareRate
                      const dNet = houseNet * (1 - agentShareRate)
                      return (
                        <>
                          <TableCell className="text-right font-mono text-sm">
                            <span className={aNet >= 0 ? 'text-success font-semibold' : 'text-destructive font-semibold'}>
                              {formatCurrency(aNet.toFixed(2))}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            <span className={dNet >= 0 ? 'text-success font-semibold' : 'text-destructive font-semibold'}>
                              {formatCurrency(dNet.toFixed(2))}
                            </span>
                          </TableCell>
                        </>
                      )
                    })()}
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                        onClick={() => setDetailBet(b)}
                        title="ดูผลรอบ"
                      >
                        <Info className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
              {totals && (
                <TableRow className="bg-secondary/30 font-semibold border-t-2 border-border">
                  <TableCell colSpan={3} className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {t.reports.totals}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatCurrency(totals.bet.toFixed(2))}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatCurrency(totals.win.toFixed(2))}
                  </TableCell>
                  <TableCell colSpan={2} />
                  <TableCell className="text-right font-mono text-sm">
                    <span className={totals.net >= 0 ? 'text-success' : 'text-destructive'}>
                      {formatCurrency(totals.net.toFixed(2))}
                    </span>
                  </TableCell>
                  {showShares && (
                    <>
                      <TableCell className="text-right font-mono text-sm">
                        <span className={totals.aNet >= 0 ? 'text-success' : 'text-destructive'}>
                          {formatCurrency(totals.aNet.toFixed(2))}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        <span className={totals.dNet >= 0 ? 'text-success' : 'text-destructive'}>
                          {formatCurrency(totals.dNet.toFixed(2))}
                        </span>
                      </TableCell>
                    </>
                  )}
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        {totalPages > 1 && !loading && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30">
            <span className="font-mono text-xs text-muted-foreground">{page} / {totalPages}</span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label={t.common.prev}>
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} aria-label={t.common.next}>
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <RoundResultDialog
        bet={detailBet}
        open={detailBet !== null}
        onClose={() => setDetailBet(null)}
      />
    </div>
  )
}
